/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useState, useCallback, useEffect, type ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from '@mui/material';
import { getColor } from '../styles';
import { Button as DefButton } from './buttons';
import { Input as DefInput } from './form-elements';
import { TwoFactorBackupCodeForm } from './forms';
import { Text } from './texts';

type Props = {
  loading: boolean;
  submitCodeButtonText: string;
  childrenAfterInput?: ReactNode;
  onCodeFill: (code: string) => Promise<void>;
  onBackupCodeVisibilityChange?: (visible: boolean) => void;
};

const TwoFACode = ({
  loading,
  submitCodeButtonText,
  childrenAfterInput,
  onCodeFill,
  onBackupCodeVisibilityChange,
}: Props) => {
  const { t } = useTranslation();
  const [codeFieldVisible, setCodeFieldVisible] = useState(true);
  const [userCode, setUserCode] = useState('');

  // Required to change title of bounding modal
  useEffect(() => {
    onBackupCodeVisibilityChange?.(!codeFieldVisible);
  }, [onBackupCodeVisibilityChange, codeFieldVisible]);

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent) => {
      if (['Enter', 'NumpadEnter'].some((name) => event.code === name)) {
        onCodeFill(userCode);
      }
    },
    [onCodeFill, userCode]
  );

  const handleChange = useCallback(
    ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) =>
      setUserCode(value.replace(/\D/g, '')),
    []
  );

  return (
    <>
      {codeFieldVisible ? (
        <>
          <Input
            inputMode={'numeric'}
            autoComplete={'one-time-code'}
            autoFocus
            label={t('TWO_FA_MODAL__inputLabel')}
            value={userCode}
            onKeyUp={handleKeyUp}
            onChange={handleChange}
          />
          {childrenAfterInput}
          <Button
            loading={loading}
            onClick={() => {
              onCodeFill(userCode);
            }}
          >
            {submitCodeButtonText}
          </Button>
          <ProceedToBackupCodesBlock>
            {t('TWO_FA_MODAL__text')}
            <br />
            <TextButton onClick={() => setCodeFieldVisible(false)}>
              {t('TWO_FA_MODAL__linkText')}
            </TextButton>
          </ProceedToBackupCodesBlock>
        </>
      ) : (
        <TwoFactorBackupCodeForm onCodeFill={onCodeFill} />
      )}
    </>
  );
};

const Input = styled(DefInput)``;

const Button = styled(DefButton)`
  width: 100%;
  margin-top: 6px;
`;

const ProceedToBackupCodesBlock = styled(Text)`
  margin-top: 34px;
  text-align: center;
  color: ${getColor('shuttleGray')};
`;

const TextButton = styled(Link)``;

export { TwoFACode };
